/*
 * Demo only: the Design System screen's DsDataTable example. The data, the
 * fake pager and the sort functions are made up to show the table's features;
 * nothing here is for use in real pages.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createColumnHelper,
  functionalUpdate,
  type RowSelectionState,
  type SortingState,
  type Updater,
} from '@tanstack/react-table';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import { Badge } from 'src/component/ds/ui/badge';
import { Button } from 'src/component/ds/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'src/component/ds/ui/select';
import { DataTableColumnHeader } from 'src/component/ds/ui/dataTableColumnHeader';
import { type DataTableFeatures } from 'src/component/ds/ui/dataTableFeatures';
import { DsDataTable } from 'src/component/ds/dataTable/DsDataTable';
import { dsSelectColumn } from 'src/component/ds/dataTable/selectColumn';
import { useDsTable } from 'src/component/ds/dataTable/useDsTable';
import type { DsTableRow } from 'src/component/ds/dataTable/types';

type ColumnSort = SortingState[number];

/* Stand-in for a paged API: slices `all` after a delay, re-sorting on request. */
function useFakePages<T> (
  all: T[],
  sortRows: (rows: T[], sort: ColumnSort) => T[],
  pageSize = 10
) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const load = useCallback((offset: number, nextSorting: SortingState) => {
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const sorted = nextSorting[0] ? sortRows(all, nextSorting[0]) : all;
      setRows((prev) => [...(offset ? prev : []), ...sorted.slice(offset, offset + pageSize)]);
      setLoading(false);
    }, 600);
  }, [all, sortRows, pageSize]);

  useEffect(() => {
    load(0, []);
    return () => clearTimeout(timer.current);
  }, [load]);

  return {
    rows,
    sorting,
    // A new sort means a new query: drop what's loaded and start from the top.
    onSortingChange: (updater: Updater<SortingState>) => {
      const next = functionalUpdate(updater, sorting);
      setSorting(next);
      setRows([]);
      load(0, next);
    },
    loadProps: {
      loading,
      hasMore: rows.length < all.length,
      onLoadMore: () => load(rows.length, sorting),
    },
  };
}

/* ---- Parent > child, grouped ---------------------------------------- */

type Counts = [number, number, number, number];
/* Group and subgroup are fields on the parent, shown once per group with `hideRepeats`. */
type TreeLevels = {
  parent: { group: string; subgroup: string; name: string; detail: string; childCount: number; counts: Counts };
  child: { name: string; detail: string; active: boolean; counts: Counts };
};
type TreeRow = DsTableRow<TreeLevels>;

const NO_COUNTS: Counts = [0, 0, 0, 0];
const sumCounts = (list: Counts[]) =>
  list.reduce<Counts>((a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2], a[3] + b[3]], NO_COUNTS);

function makeParent (i: number, group: string, subgroup: string): TreeRow {
  const children = Array.from({ length: 2 + (i % 4) }, (__, j) => ({
    id: `child-${i}-${j}`,
    level: 'child' as const,
    data: {
      name: `Child ${i + 1}.${j + 1}`,
      detail: 'Detail text',
      active: (i + j) % 3 !== 0,
      counts: (j === 0 ? [i % 3, 1, i % 2, 2] : NO_COUNTS) as Counts,
    },
  }));
  return {
    id: `parent-${i}`,
    level: 'parent',
    data: {
      group,
      subgroup,
      name: `Parent ${i + 1}`,
      detail: 'Detail text',
      childCount: children.length,
      counts: sumCounts(children.map((c) => c.data.counts)),
    },
    children,
  };
}

/* 32 parents, sorted by group then subgroup: 8 groups x 2 subgroups x 2 parents. */
const PARENTS: TreeRow[] = Array.from({ length: 32 }, (_, i) => {
  const g = Math.floor(i / 4);
  const sg = Math.floor(i / 2) % 2;
  return makeParent(i, `Group ${String.fromCharCode(65 + g)}`, `Subgroup ${sg + 1}`);
});

const COUNT_BADGE_CLASSES = [
  'ds-bg-severity-highest ds-text-white',
  'ds-bg-severity-high ds-text-white',
  'ds-bg-severity-medium ds-text-white',
  'ds-bg-primary ds-text-primary-foreground',
];

function CountBadges ({ counts }: { counts: Counts }) {
  return (
    <div className='ds-flex ds-gap-1'>
      {counts.map((count, i) => (
        <Badge key={i} variant='outline' className={count ? COUNT_BADGE_CLASSES[i] : undefined}>
          {count}
        </Badge>
      ))}
    </div>
  );
}

const ActiveDot = ({ active }: { active: boolean }) => (
  <span
    aria-label={active ? 'active' : 'inactive'}
    className={active ? 'ds-inline-block ds-size-2 ds-rounded-full ds-bg-severity-medium' : 'ds-inline-block ds-size-2 ds-rounded-full ds-bg-muted-foreground'}
  />
);

const tree = createColumnHelper<DataTableFeatures, TreeRow>();

/*
 * `meta.levels` keeps the parent-only columns blank on child rows (and drops
 * the borders under a parent). The accessors' `level` checks only narrow `data`.
 */
const TREE_COLUMNS = tree.columns([
  dsSelectColumn<TreeRow>(),
  tree.accessor((r) => (r.level === 'parent' ? r.data.group : undefined), {
    id: 'group',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Group' />,
    meta: { levels: ['parent'], hideRepeats: true },
  }),
  tree.accessor((r) => (r.level === 'parent' ? r.data.subgroup : undefined), {
    id: 'subgroup',
    header: 'Subgroup',
    meta: { levels: ['parent'], hideRepeats: true },
  }),
  tree.accessor((r) => (r.level === 'parent' ? r.data.name : undefined), {
    id: 'parent',
    header: 'Parent',
    meta: { levels: ['parent'] },
    cell: ({ row }) => {
      const r = row.original;
      if (r.level !== 'parent') return null;
      return (
        <div className='ds-flex ds-flex-col'>
          <span className='ds-underline'>{r.data.name}</span>
          <span className='ds-text-muted-foreground ds-font-normal'>{r.data.detail}</span>
        </div>
      );
    },
  }),
  tree.accessor((r) => (r.level === 'parent' ? r.data.childCount : r.data.name), {
    id: 'child',
    header: 'Child',
    cell: ({ row }) => {
      const r = row.original;
      if (r.level === 'parent') return `${r.data.childCount} total`;
      return (
        <div className='ds-flex ds-items-center ds-gap-2'>
          <ActiveDot active={r.data.active} />
          <div className='ds-flex ds-flex-col'>
            <span className='ds-underline'>{r.data.name}</span>
            <span>{r.data.detail}</span>
          </div>
        </div>
      );
    },
  }),
  tree.accessor((r) => r.data.counts, {
    id: 'counts',
    header: 'Counts',
    cell: ({ getValue }) => <CountBadges counts={getValue()} />,
  }),
  tree.display({
    id: 'open',
    header: () => <span className='ds-sr-only'>Open</span>,
    meta: { className: 'ds-w-8 ds-text-muted-foreground' },
    cell: () => <ChevronRightIcon fontSize='small' aria-hidden='true' />,
  }),
]);

/* The "server" sorts by group; a stable sort keeps subgroups together within each group. */
const sortByGroup = (rows: TreeRow[], sort: ColumnSort) => [...rows].sort((a, b) => {
  const groupOf = (r: TreeRow) => (r.level === 'parent' ? r.data.group : '');
  return groupOf(a).localeCompare(groupOf(b)) * (sort.desc ? -1 : 1);
});

function GroupedTreeTable () {
  // Pages of 5 parents: groups split across pages, and hideRepeats still lines up.
  const { rows, sorting, onSortingChange, loadProps } = useFakePages(PARENTS, sortByGroup, 5);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [clicked, setClicked] = useState('');

  const table = useDsTable({
    data: rows,
    columns: TREE_COLUMNS,
    state: { sorting, rowSelection },
    onSortingChange,
    onRowSelectionChange: setRowSelection,
  });

  return (
    <div className='ds-flex ds-flex-col ds-gap-2'>
      <DsDataTable
        table={table}
        {...loadProps}
        onRowClick={(row) => setClicked(row.id)}
        containerClassName='ds-h-[400px]'
      />
      <p className='ds-text-sm ds-text-muted-foreground'>
        {Object.keys(rowSelection).length} selected · last clicked: {clicked || '—'}
      </p>
    </div>
  );
}

/* ---- Flat list ------------------------------------------------------- */

type Priority = 'high' | 'medium' | 'low';
type Status = 'open' | 'pending' | 'closed';
type ItemData = {
  name: string;
  category: string;
  priority: Priority;
  value: string;
  status: Status;
  note: string;
};
type ItemRow = DsTableRow<{ item: ItemData }>;

const PRIORITIES: Priority[] = ['high', 'medium', 'low'];
const PRIORITY_CELL: Record<Priority, string> = {
  high: 'ds-bg-severity-highest ds-text-white',
  medium: 'ds-bg-severity-high ds-text-white',
  low: 'ds-bg-severity-medium ds-text-white',
};

const ITEMS: ItemRow[] = Array.from({ length: 40 }, (_, i) => ({
  id: `item-${i}`,
  level: 'item',
  data: {
    name: `Item ${i + 1}`,
    category: `Category ${String.fromCharCode(65 + (i % 4))}`,
    priority: PRIORITIES[i % 3],
    value: `${i % 9} / 30`,
    status: i % 5 === 2 ? 'pending' : 'open',
    note: `Note for item ${i + 1}.`,
  },
}));

function StatusSelect ({ initial }: { initial: Status }) {
  const [value, setValue] = useState(initial);
  return (
    <div className='ds-w-28'>
      <Select value={value} onValueChange={(v) => setValue(v as Status)}>
        <SelectTrigger size='sm' aria-label='Status'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value='open'>Open</SelectItem>
          <SelectItem value='pending'>Pending</SelectItem>
          <SelectItem value='closed'>Closed</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

const item = createColumnHelper<DataTableFeatures, ItemRow>();

const ITEM_COLUMNS = item.columns([
  dsSelectColumn<ItemRow>(),
  item.accessor((r) => r.data.name, {
    id: 'name',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
  }),
  item.accessor((r) => r.data.category, { id: 'category', header: 'Category' }),
  item.accessor((r) => r.data.priority, {
    id: 'priority',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Priority' />,
    meta: {
      className: 'ds-capitalize',
      cellClassName: (cell) => PRIORITY_CELL[cell.row.original.data.priority],
    },
  }),
  item.accessor((r) => r.data.value, { id: 'value', header: 'Value' }),
  item.display({
    id: 'status',
    header: 'Status',
    meta: { interactive: true },
    cell: ({ row }) => <StatusSelect initial={row.original.data.status} />,
  }),
  item.display({
    id: 'action',
    header: 'Action',
    meta: { interactive: true },
    cell: ({ row }) => (
      <Button variant='outline' size='icon-sm' aria-label={`Reset ${row.original.data.name}`}>
        <RestartAltIcon fontSize='small' aria-hidden='true' />
      </Button>
    ),
  }),
  item.accessor((r) => r.data.note, {
    id: 'note',
    header: 'Note',
    meta: { className: 'ds-bg-muted ds-max-w-[220px]' },
  }),
]);

const PRIORITY_RANK: Record<Priority, number> = { high: 0, medium: 1, low: 2 };
const sortItems = (rows: ItemRow[], sort: ColumnSort) => [...rows].sort((a, b) => {
  const diff = sort.id === 'priority'
    ? PRIORITY_RANK[a.data.priority] - PRIORITY_RANK[b.data.priority]
    : a.data.name.localeCompare(b.data.name, undefined, { numeric: true });
  return diff * (sort.desc ? -1 : 1);
});

function FlatListTable () {
  const { rows, sorting, onSortingChange, loadProps } = useFakePages(ITEMS, sortItems);
  const table = useDsTable({
    data: rows,
    columns: ITEM_COLUMNS,
    state: { sorting },
    onSortingChange,
  });

  return (
    <div className='ds-flex ds-flex-col ds-gap-2'>
      <DsDataTable
        table={table}
        {...loadProps}
        rowClassName={(row) => (row.original.data.status === 'pending' ? 'ds-bg-muted' : undefined)}
        onRowClick={() => undefined}
        containerClassName='ds-h-[400px]'
      />
    </div>
  );
}

/* Wrapper over the shadcn table: hierarchy, custom cells, colouring, scroll loading. */
export function DsDataTableDemo () {
  return (
    <div className='ds-flex ds-flex-col ds-gap-6'>
      <div className='ds-flex ds-flex-col ds-gap-2'>
        <h3 className='ds-text-base ds-font-medium'>Parent › child, grouped by group and subgroup (lazy loaded)</h3>
        <GroupedTreeTable />
      </div>
      <div className='ds-flex ds-flex-col ds-gap-2'>
        <h3 className='ds-text-base ds-font-medium'>Flat list (interactive cells, cell/column/row colour)</h3>
        <FlatListTable />
      </div>
    </div>
  );
}
